import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { XYPlot, YAxis } from 'react-vis';
import 'react-vis/dist/style.css';
import UpstreamChartLabel from './UpstreamChartLabel';

interface Margin {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface ScrollableXYPlotProps {
  height: number;
  plotWidth: number;
  margin: Margin;
  yDomain: number[];
  xDomain?: number[];
  xPadding?: number;
  yTitle: string;
  xTitle?: string;
  yTickFormat: (v: number) => string;
  children: React.ReactNode;
}

// Renders an XYPlot that can grow wider than its container so all data points are visible via
// horizontal scrolling, while keeping the Y-axis (and its title) pinned on the left. The Y-axis is
// its own fixed-width plot in a separate column; the scrollable plot shares the same height, vertical
// margins and yDomain so its horizontal gridlines line up exactly with the pinned axis ticks.
const ScrollableXYPlot = (props: ScrollableXYPlotProps) => {
  const { height, plotWidth, margin, yDomain, xDomain, xPadding = 0, yTitle, xTitle, yTickFormat, children } = props;
  const axisColWidth = margin.left + 4;
  const axisMargin = { left: margin.left, right: 0, top: margin.top, bottom: margin.bottom };
  const plotMargin = { left: 0, right: 0, top: margin.top, bottom: margin.bottom };

  const scrollRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setContainerWidth(el.clientWidth);
    const maxScroll = el.scrollWidth - el.clientWidth;
    // Anchor to the most recent years once the container first has a real, scrollable width. This is
    // done here (rather than only on mount) so charts that mount while hidden — e.g. in a collapsed
    // section or an inactive tab — still start at the end once they become visible.
    if (!didInitialScroll.current && el.clientWidth > 0 && maxScroll > 1) {
      el.scrollLeft = el.scrollWidth;
      didInitialScroll.current = true;
    }
    setIsScrollable(maxScroll > 1);
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
  }, []);

  // Re-anchor to the most recent years whenever the dataset width changes (new data loaded).
  useLayoutEffect(() => {
    didInitialScroll.current = false;
    updateScrollState();
  }, [plotWidth, updateScrollState]);

  // Keep the arrow button state in sync with scroll position and container size changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scrollByPage = (direction: number) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  // Vertically center the arrows over the plotting area (above the bottom axis margin).
  const arrowTop = margin.top + (height - margin.top - margin.bottom) / 2;
  const arrowBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: arrowTop,
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1px solid #ccc',
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    lineHeight: 1,
    padding: 0,
    color: '#333',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <div style={{ flex: '0 0 auto' }}>
        <XYPlot
          height={height}
          width={axisColWidth}
          yPadding={1}
          margin={axisMargin}
          yDomain={yDomain}
          xDomain={[0, 1]}
          dontCheckIfEmpty
        >
          <YAxis tickFormat={yTickFormat} />
          <UpstreamChartLabel
            text={yTitle}
            className="alt-y-label"
            xPercent={0}
            yPercent={0.5}
            xPixels={9}
            style={{
              textAnchor: 'middle',
              transform: 'rotate(-90)',
            }}
          />
        </XYPlot>
      </div>
      <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
        {isScrollable ? (
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollByPage(-1)}
            disabled={!canScrollLeft}
            style={{
              ...arrowBaseStyle,
              left: 4,
              opacity: canScrollLeft ? 1 : 0.4,
              cursor: canScrollLeft ? 'pointer' : 'default',
            }}
          >
            {'‹'}
          </button>
        ) : null}
        {isScrollable ? (
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollByPage(1)}
            disabled={!canScrollRight}
            style={{
              ...arrowBaseStyle,
              right: 4,
              opacity: canScrollRight ? 1 : 0.4,
              cursor: canScrollRight ? 'pointer' : 'default',
            }}
          >
            {'›'}
          </button>
        ) : null}
        <div ref={scrollRef} style={{ overflowX: 'auto' }}>
          <XYPlot
            height={height}
            width={Math.max(plotWidth, containerWidth)}
            yPadding={1}
            xPadding={xPadding}
            margin={plotMargin}
            yDomain={yDomain}
            xDomain={xDomain}
          >
            {children}
          </XYPlot>
        </div>
        {xTitle ? (
          <div
            style={{
              position: 'absolute',
              bottom: 4,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: 11,
              color: '#6b6b76',
              pointerEvents: 'none',
            }}
          >
            {xTitle}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ScrollableXYPlot;
